import React, { useEffect, useState } from 'react';
import { AppSidebar } from '@/components/app-sidebar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import {
  BarChart3,
  TrendingUp,
  Target,
  Users,
  Calendar,
  Award,
  CheckCircle,
  CalendarCheck,
  Trophy
} from 'lucide-react';
import { fetchLeadsForCurrentUser } from '@/services/leadsService';
import { getUsers, User } from '@/services/userService';
import { Lead, HistoryEntry } from '@/components/lead-card';
import {
  format,
  startOfDay,
  startOfWeek,
  startOfMonth,
  startOfYear,
  differenceInCalendarDays
} from 'date-fns';
import { de } from 'date-fns/locale';
import { logger } from '@/lib/logger';

// ---------- KPI helpers ----------

const parseDate = (value: unknown): Date | null => {
  if (!value) return null;
  const d = new Date(value as string);
  return isNaN(d.getTime()) ? null : d;
};

// Strict: only true createdAt counts as "lead generated".
const getCreatedAt = (lead: Lead): Date | null => parseDate(lead.createdAt);

// Find the date a lead's status was changed TO `targetStatus` (most recent occurrence).
// Looks ONLY at genuine status-change entries in history. No lastUpdated/createdAt
// fallback — otherwise an old lead edited today would be miscounted as "won/scheduled
// today". Leads without recorded history simply don't count toward time-bucketed KPIs.
const getStatusChangeDate = (lead: Lead, targetStatus: string): Date | null => {
  const history: HistoryEntry[] = Array.isArray(lead.history) ? lead.history : [];
  let latest: Date | null = null;
  for (const entry of history) {
    const changes = (entry as unknown as { changes?: Array<{ field?: string; newValue?: unknown }> }).changes;
    if (!Array.isArray(changes)) continue;
    const hit = changes.some(
      c => c && c.field === 'status' && c.newValue === targetStatus
    );
    if (!hit) continue;
    const d = parseDate(entry.date);
    if (d && (!latest || d > latest)) latest = d;
  }
  return latest;
};

const isOnOrAfter = (date: Date | null, ref: Date): boolean =>
  !!date && date >= ref;

// ---------- Types ----------

interface EmployeeStats {
  id: string;
  name: string;
  generatedToday: number;
  generatedWeek: number;
  generatedMonth: number;
  scheduledToday: number;
  scheduledWeek: number;
  scheduledMonth: number;
  wonToday: number;
  wonWeek: number;
  wonMonth: number;
  totalAssigned: number;
  conversionRate: number; // won this month / generated this month
}

interface PerformanceMetrics {
  // Date-bucketed totals
  generatedToday: number;
  generatedWeek: number;
  generatedMonth: number;
  generatedYear: number;

  scheduledToday: number;
  scheduledWeek: number;
  scheduledMonth: number;

  wonToday: number;
  wonWeek: number;
  wonMonth: number;

  avgGeneratedPerDay: number;   // generatedMonth / days elapsed in month
  avgScheduledPerDay: number;   // scheduledMonth / days elapsed in month

  conversionRate: number;       // wonMonth / generatedMonth

  totalLeads: number;
  leadsWithoutCreatedAt: number;
  wonWithoutStatusHistory: number;
  scheduledWithoutStatusHistory: number;

  // Status snapshot for "current month" (date = createdAt in current month)
  statusBreakdown: {
    new: number;
    oldContact: number;
    notReached: number;
    followUp: number;
    scheduled: number;
    inProgress: number;
    won: number;
    lost: number;
    wrongData: number;
    deleteData: number;
  };

  employeeStats: EmployeeStats[];
}

const emptyMetrics: PerformanceMetrics = {
  generatedToday: 0,
  generatedWeek: 0,
  generatedMonth: 0,
  generatedYear: 0,
  scheduledToday: 0,
  scheduledWeek: 0,
  scheduledMonth: 0,
  wonToday: 0,
  wonWeek: 0,
  wonMonth: 0,
  avgGeneratedPerDay: 0,
  avgScheduledPerDay: 0,
  conversionRate: 0,
  totalLeads: 0,
  leadsWithoutCreatedAt: 0,
  wonWithoutStatusHistory: 0,
  scheduledWithoutStatusHistory: 0,
  statusBreakdown: {
    new: 0, oldContact: 0, notReached: 0, followUp: 0,
    scheduled: 0, inProgress: 0, won: 0, lost: 0,
    wrongData: 0, deleteData: 0
  },
  employeeStats: []
};

// ---------- Component ----------

export default function PerformanceMetricsPage() {
  const { currentUser } = useAuth();
  const { toast } = useToast();
  const [metrics, setMetrics] = useState<PerformanceMetrics>(emptyMetrics);
  const [isLoading, setIsLoading] = useState(true);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  useEffect(() => {
    const loadMetrics = async () => {
      if (!currentUser) return;
      setIsLoading(true);
      try {
        const fetchedUsers = await getUsers(currentUser.tenantId);
        setUsers(fetchedUsers);
        const userLeads = await fetchLeadsForCurrentUser(
          currentUser.id,
          currentUser.role,
          currentUser.tenantId
        );
        setLeads(userLeads);
        setMetrics(calculateMetrics(userLeads, fetchedUsers));
      } catch (error) {
        logger.error('Error loading performance metrics:', error);
        toast({
          title: 'Fehler beim Laden der Metriken',
          description: 'Die Performance-Daten konnten nicht geladen werden.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };
    loadMetrics();
  }, [currentUser, toast]);

  useEffect(() => {
    if (leads.length > 0 && users.length > 0) {
      setMetrics(calculateMetrics(leads, users));
    }
  }, [leads, users]);

  const calculateMetrics = (allLeads: Lead[], allUsers: User[]): PerformanceMetrics => {
    const now = new Date();
    const todayStart = startOfDay(now);
    const weekStart = startOfWeek(now, { weekStartsOn: 1 });
    const monthStart = startOfMonth(now);
    const yearStart = startOfYear(now);

    // Exclude developer accounts entirely from this report. Developer leads are
    // typically test data and would distort KPIs. Mirrors TeamOverviewPage.
    const developerIds = new Set(
      allUsers.filter(u => u.role === 'developer').map(u => u.id)
    );
    const visibleLeads = allLeads.filter(l => !developerIds.has(l.assignedTo));

    // 1. Generated leads — strictly via createdAt
    let generatedToday = 0, generatedWeek = 0, generatedMonth = 0, generatedYear = 0;
    let leadsWithoutCreatedAt = 0;
    for (const lead of visibleLeads) {
      const created = getCreatedAt(lead);
      if (!created) { leadsWithoutCreatedAt++; continue; }
      if (isOnOrAfter(created, todayStart)) generatedToday++;
      if (isOnOrAfter(created, weekStart)) generatedWeek++;
      if (isOnOrAfter(created, monthStart)) generatedMonth++;
      if (isOnOrAfter(created, yearStart)) generatedYear++;
    }

    // 2. Scheduled — leads whose status was changed to 'scheduled' inside the period
    let scheduledToday = 0, scheduledWeek = 0, scheduledMonth = 0;
    let scheduledWithoutStatusHistory = 0;
    for (const lead of visibleLeads) {
      const d = getStatusChangeDate(lead, 'scheduled');
      if (!d) {
        if (lead.status === 'scheduled') scheduledWithoutStatusHistory++;
        continue;
      }
      if (isOnOrAfter(d, todayStart)) scheduledToday++;
      if (isOnOrAfter(d, weekStart)) scheduledWeek++;
      if (isOnOrAfter(d, monthStart)) scheduledMonth++;
    }

    // 3. Won — leads whose status was changed to 'won' inside the period
    let wonToday = 0, wonWeek = 0, wonMonth = 0;
    let wonWithoutStatusHistory = 0;
    for (const lead of visibleLeads) {
      const d = getStatusChangeDate(lead, 'won');
      if (!d) {
        if (lead.status === 'won') wonWithoutStatusHistory++;
        continue;
      }
      if (isOnOrAfter(d, todayStart)) wonToday++;
      if (isOnOrAfter(d, weekStart)) wonWeek++;
      if (isOnOrAfter(d, monthStart)) wonMonth++;
    }

    // 4. Average per day — based on elapsed days in current month (at least 1)
    const elapsedDays = Math.max(1, differenceInCalendarDays(now, monthStart) + 1);
    const avgGeneratedPerDay = generatedMonth / elapsedDays;
    const avgScheduledPerDay = scheduledMonth / elapsedDays;

    // 5. Conversion rate — wonMonth / generatedMonth
    const conversionRate = generatedMonth > 0 ? (wonMonth / generatedMonth) * 100 : 0;

    // 6. Status snapshot — current tenant inventory across all leads.
    // Independent from time buckets so admins always see real distribution
    // of all leads in the system, not only those created this month.
    const statusBreakdown = {
      new:         visibleLeads.filter(l => l.status === 'new').length,
      oldContact:  visibleLeads.filter(l => l.status === 'old-contact').length,
      notReached:  visibleLeads.filter(l => l.status === 'not-reached').length,
      followUp:    visibleLeads.filter(l => l.status === 'follow-up').length,
      scheduled:   visibleLeads.filter(l => l.status === 'scheduled').length,
      inProgress:  visibleLeads.filter(l => l.status === 'in-progress').length,
      won:         visibleLeads.filter(l => l.status === 'won').length,
      lost:        visibleLeads.filter(l => l.status === 'lost').length,
      wrongData:   visibleLeads.filter(l => l.status === 'wrong-data').length,
      deleteData:  visibleLeads.filter(l => l.status === 'delete-data').length
    };

    // 7. Per employee — developers are excluded entirely
    const employeeStats: EmployeeStats[] = allUsers
      .filter(u => u.active !== false && u.role !== 'developer')
      .map(user => {
        const userLeads = visibleLeads.filter(l => l.assignedTo === user.id);

        let gToday = 0, gWeek = 0, gMonth = 0;
        for (const l of userLeads) {
          const c = getCreatedAt(l);
          if (!c) continue;
          if (isOnOrAfter(c, todayStart)) gToday++;
          if (isOnOrAfter(c, weekStart)) gWeek++;
          if (isOnOrAfter(c, monthStart)) gMonth++;
        }

        let sToday = 0, sWeek = 0, sMonth = 0;
        for (const l of userLeads) {
          const d = getStatusChangeDate(l, 'scheduled');
          if (!d) continue;
          if (isOnOrAfter(d, todayStart)) sToday++;
          if (isOnOrAfter(d, weekStart)) sWeek++;
          if (isOnOrAfter(d, monthStart)) sMonth++;
        }

        let wToday = 0, wWeek = 0, wMonth = 0;
        for (const l of userLeads) {
          const d = getStatusChangeDate(l, 'won');
          if (!d) continue;
          if (isOnOrAfter(d, todayStart)) wToday++;
          if (isOnOrAfter(d, weekStart)) wWeek++;
          if (isOnOrAfter(d, monthStart)) wMonth++;
        }

        const conv = gMonth > 0 ? (wMonth / gMonth) * 100 : 0;

        return {
          id: user.id,
          name: user.name || user.email || 'Unbekannt',
          generatedToday: gToday,
          generatedWeek: gWeek,
          generatedMonth: gMonth,
          scheduledToday: sToday,
          scheduledWeek: sWeek,
          scheduledMonth: sMonth,
          wonToday: wToday,
          wonWeek: wWeek,
          wonMonth: wMonth,
          totalAssigned: userLeads.length,
          conversionRate: conv
        };
      })
      .sort((a, b) => b.generatedMonth - a.generatedMonth);

    return {
      generatedToday, generatedWeek, generatedMonth, generatedYear,
      scheduledToday, scheduledWeek, scheduledMonth,
      wonToday, wonWeek, wonMonth,
      avgGeneratedPerDay, avgScheduledPerDay,
      conversionRate,
      totalLeads: visibleLeads.length,
      leadsWithoutCreatedAt,
      wonWithoutStatusHistory,
      scheduledWithoutStatusHistory,
      statusBreakdown,
      employeeStats
    };
  };

  // ---------- Render ----------

  const renderMetricCard = (
    title: string,
    today: number,
    week: number,
    month: number,
    Icon: React.ComponentType<{ className?: string }>
  ) => (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-2xl font-bold">{today}</p>
            <p className="text-xs text-muted-foreground">Heute</p>
          </div>
          <div>
            <p className="text-2xl font-bold">{week}</p>
            <p className="text-xs text-muted-foreground">Woche</p>
          </div>
          <div>
            <p className="text-2xl font-bold">{month}</p>
            <p className="text-xs text-muted-foreground">Monat</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="h-screen bg-background flex overflow-hidden">
      <AppSidebar />
      <div className="flex-1 overflow-y-auto">
        <div className="container mx-auto p-6 space-y-6 pb-12">
          {/* Header */}
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Performance Metriken</h1>
              <p className="text-muted-foreground mt-2">
                Generierte Leads, Termine und gewonnene Kunden — heute, diese Woche, diesen Monat.
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-muted-foreground bg-muted px-3 py-1 rounded-md">
                Aktueller Monat: {format(new Date(), 'MMMM yyyy', { locale: de })}
              </span>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="mt-2 text-muted-foreground">Lade Performance-Daten...</p>
            </div>
          ) : (
            <>
              {/* Summary (formerly footer) */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Award className="h-5 w-5 mr-2" />
                    Zusammenfassung
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Generiert / Tag</p>
                      <p className="text-lg font-bold">{metrics.avgGeneratedPerDay.toFixed(1)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Termine / Tag</p>
                      <p className="text-lg font-bold">{metrics.avgScheduledPerDay.toFixed(1)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Konversion</p>
                      <p className="text-lg font-bold">{metrics.conversionRate.toFixed(1)}%</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Leads gesamt</p>
                      <p className="text-lg font-bold">{metrics.totalLeads}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Year + Month overview */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Leads dieses Jahr</CardTitle>
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{metrics.generatedYear}</div>
                    <p className="text-xs text-muted-foreground">von {metrics.totalLeads} insgesamt</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Leads diesen Monat</CardTitle>
                    <BarChart3 className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{metrics.generatedMonth}</div>
                    <p className="text-xs text-muted-foreground">
                      Ø {metrics.avgGeneratedPerDay.toFixed(1)} pro Tag
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Konversionsrate</CardTitle>
                    <Target className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{metrics.conversionRate.toFixed(1)}%</div>
                    <p className="text-xs text-muted-foreground">
                      {metrics.wonMonth} gewonnen / {metrics.generatedMonth} generiert (Monat)
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Ø Termine / Tag</CardTitle>
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{metrics.avgScheduledPerDay.toFixed(1)}</div>
                    <p className="text-xs text-muted-foreground">
                      {metrics.scheduledMonth} Termine im Monat
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Three KPI cards: generated / scheduled / won, each split today/week/month */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {renderMetricCard('Generierte Leads', metrics.generatedToday, metrics.generatedWeek, metrics.generatedMonth, BarChart3)}
                {renderMetricCard('Vereinbarte Termine', metrics.scheduledToday, metrics.scheduledWeek, metrics.scheduledMonth, CalendarCheck)}
                {renderMetricCard('Gewonnene Kunden', metrics.wonToday, metrics.wonWeek, metrics.wonMonth, Trophy)}
              </div>

              {/* Status breakdown + employees */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <CheckCircle className="h-5 w-5 mr-2" />
                      Aktueller Lead-Bestand
                    </CardTitle>
                    <CardDescription>
                      Alle Leads im System nach aktuellem Status (zeitunabhängig)
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {([
                      ['Neu', metrics.statusBreakdown.new, 'bg-blue-500'],
                      ['Terminiert', metrics.statusBreakdown.scheduled, 'bg-emerald-500'],
                      ['In Prozess', metrics.statusBreakdown.inProgress, 'bg-orange-500'],
                      ['Wiedervorlage', metrics.statusBreakdown.followUp, 'bg-purple-500'],
                      ['Nicht erreicht', metrics.statusBreakdown.notReached, 'bg-amber-500'],
                      ['Gewonnen', metrics.statusBreakdown.won, 'bg-green-500'],
                      ['Verloren', metrics.statusBreakdown.lost, 'bg-red-500'],
                      ['Altkontakt', metrics.statusBreakdown.oldContact, 'bg-slate-500'],
                      ['Falsche Daten', metrics.statusBreakdown.wrongData, 'bg-gray-400'],
                      ['Zur Löschung', metrics.statusBreakdown.deleteData, 'bg-red-600'],
                    ] as Array<[string, number, string]>).map(([label, value, color]) => (
                      <div key={label} className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className={`w-3 h-3 ${color} rounded-full mr-2`}></div>
                          <span className="text-sm">{label}</span>
                        </div>
                        <span className="font-medium">{value}</span>
                      </div>
                    ))}
                    <div className="pt-2 border-t">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Gesamt im System</span>
                        <span className="font-bold">{metrics.totalLeads}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Users className="h-5 w-5 mr-2" />
                      Mitarbeiterübersicht
                    </CardTitle>
                    <CardDescription>
                      Generiert / Termine / Gewonnen — heute, Woche, Monat. Konversion =
                      Gewonnen pro Monat ÷ Generiert pro Monat (Monats-Performance,
                      keine Kohortenanalyse).
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                      {metrics.employeeStats.length > 0 ? (
                        metrics.employeeStats.map((stat) => (
                          <div key={stat.id} className="pb-3 border-b last:border-0">
                            <div className="flex items-center justify-between mb-2">
                              <p className="font-medium text-sm">{stat.name}</p>
                              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                <span>{stat.totalAssigned} zugewiesen</span>
                                <span>Konversion: {stat.conversionRate.toFixed(1)}%</span>
                              </div>
                            </div>
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-muted-foreground">
                                    <th className="text-left font-normal py-1 pr-2">Zeitraum</th>
                                    <th className="text-center font-normal py-1 px-1">Generiert</th>
                                    <th className="text-center font-normal py-1 px-1">Termine</th>
                                    <th className="text-center font-normal py-1 pl-1">Gewonnen</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    <td className="py-1 pr-2 font-medium">Heute</td>
                                    <td className="text-center bg-blue-50 rounded-sm py-1">{stat.generatedToday}</td>
                                    <td className="text-center bg-emerald-50 rounded-sm py-1">{stat.scheduledToday}</td>
                                    <td className="text-center bg-green-50 rounded-sm py-1">{stat.wonToday}</td>
                                  </tr>
                                  <tr>
                                    <td className="py-1 pr-2 font-medium">Woche</td>
                                    <td className="text-center bg-blue-50 rounded-sm py-1">{stat.generatedWeek}</td>
                                    <td className="text-center bg-emerald-50 rounded-sm py-1">{stat.scheduledWeek}</td>
                                    <td className="text-center bg-green-50 rounded-sm py-1">{stat.wonWeek}</td>
                                  </tr>
                                  <tr>
                                    <td className="py-1 pr-2 font-medium">Monat</td>
                                    <td className="text-center bg-blue-100 rounded-sm py-1 font-semibold">{stat.generatedMonth}</td>
                                    <td className="text-center bg-emerald-100 rounded-sm py-1 font-semibold">{stat.scheduledMonth}</td>
                                    <td className="text-center bg-green-100 rounded-sm py-1 font-semibold">{stat.wonMonth}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          Keine Mitarbeiterdaten verfügbar
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

            </>
          )}
        </div>
      </div>
    </div>
  );
}
